import { DateTime, Interval } from 'luxon';

/**
 * Parse an ISO string into a validated DateTime<true>.
 * Throws if the string is not valid ISO.
 *
 * Use raw DateTime.fromISO() when you need to handle invalid dates gracefully.
 */
export function fromISO(iso: string): DateTime<true> {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) {
    throw new Error(`Invalid ISO string: "${iso}" (${dt.invalidReason})`);
  }
  return dt as DateTime<true>;
}

/**
 * Parse an ISO string with zone information into a validated DateTime<true>.
 */
export function fromISOWithZone(iso: string, opts?: { zone?: string }): DateTime<true> {
  const dt = DateTime.fromISO(iso, { setZone: true, ...opts });
  if (!dt.isValid) {
    throw new Error(`Invalid ISO string: "${iso}" (${dt.invalidReason})`);
  }
  return dt as DateTime<true>;
}

/**
 * Parse a JS Date into a validated DateTime<true>.
 */
export function fromJSDate(date: Date): DateTime<true> {
  const dt = DateTime.fromJSDate(date);
  if (!dt.isValid) {
    throw new Error(`Invalid JS Date (${dt.invalidReason})`);
  }
  return dt as DateTime<true>;
}

/**
 * Create a validated DateTime<true> from epoch milliseconds.
 */
export function fromMillis(millis: number): DateTime<true> {
  const dt = DateTime.fromMillis(millis);
  if (!dt.isValid) {
    throw new Error(`Invalid millis value: ${millis} (${dt.invalidReason})`);
  }
  return dt as DateTime<true>;
}

/**
 * Create a validated Interval from two DateTimes.
 * Both inputs must be valid DateTimes — the resulting Interval is guaranteed valid.
 */
export function interval(start: DateTime<true>, end: DateTime<true>): Interval<true> {
  return Interval.fromDateTimes(start, end) as Interval<true>;
}
